import React from 'react';
import { Link } from 'react-router-dom';
import { Key, ArrowRight, CheckCircle, Zap, Lock, Settings } from 'lucide-react';

/**
 * Home Page
 * 
 * Landing page for the IAM Playground - styled to match main IDNTY website
 */
export function Home() {
  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="section-padding bg-white">
        <div className="container text-center">
          <h1 className="hero-title mb-4">
            <span className="text-[#D4AF37]">IAM</span> PLAYGROUND
          </h1>
          <p className="text-xl text-[#333333] mb-8 max-w-2xl mx-auto">
            Need an app to test out your IdP? We've got you covered — well, almost. 
            <span className="text-[#D4AF37] font-semibold"> Configure your SAML or OIDC integration</span> and 
            see what your Identity Provider actually sends back.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/configs" className="bg-[#D4AF37] text-white px-8 py-4 rounded-md font-bold text-lg hover:bg-[#B8962E] transition-colors flex items-center">
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a 
              href="#features" 
              className="border-2 border-[#333333] text-[#333333] px-8 py-4 rounded-md font-bold text-lg hover:bg-[#333333] hover:text-white transition-colors"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section id="features" className="section-padding bg-light-gray">
        <div className="container">
          <h2 className="section-title text-center mb-8">WHAT YOU CAN TEST</h2>
          <div className="grid-3">
            <FeatureCard
              icon={Key}
              title="SAML 2.0 Testing"
              description="Test your SAML Service Provider configuration with any Identity Provider. Validate assertions, attributes, and signatures."
            />
            <FeatureCard
              icon={Zap}
              title="OIDC Testing"
              description="Test OpenID Connect flows including Authorization Code, PKCE, and more. Validate ID Tokens and UserInfo responses."
            />
            <FeatureCard
              icon={Lock}
              title="Isolated Sessions"
              description="Each test runs in an isolated session with 15-minute TTL. No cross-user data leakage."
            />
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <h2 className="section-title text-center mb-8">HOW IT WORKS</h2>
          <div className="max-w-2xl mx-auto">
            <div className="space-y-6">
              <Step
                number={1}
                title="Create Configuration"
                description="Set up your SAML SP or OIDC RP configuration with your Identity Provider details."
              />
              <Step
                number={2}
                title="Initiate Test"
                description="Start a test flow which redirects to your configured Identity Provider."
              />
              <Step
                number={3}
                title="Authenticate"
                description="Complete authentication with your IdP using any test account."
              />
              <Step
                number={4}
                title="View Results"
                description="Inspect the claims, attributes, and tokens returned by your IdP."
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Protocols Section */}
      <section className="section-padding" style={{ background: 'hsl(var(--charcoal))' }}>
        <div className="container">
          <h2 className="section-title text-center mb-8" style={{ color: 'hsl(var(--white))' }}>SUPPORTED PROTOCOLS</h2>
          <div className="grid-2 max-w-4xl mx-auto">
            <ProtocolCard
              title="SAML 2.0"
              features={[
                'HTTP-Redirect binding',
                'HTTP-POST binding',
                'Signed AuthnRequests',
                'Attribute extraction',
                'Metadata generation',
              ]}
            />
            <ProtocolCard
              title="OpenID Connect"
              features={[
                'Authorization Code flow',
                'PKCE support',
                'ID Token validation',
                'UserInfo endpoint',
                'Discovery support',
              ]}
            />
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section-padding" style={{ background: 'hsl(var(--black))' }}>
        <div className="container text-center">
          <h2 className="section-title mb-4" style={{ color: 'hsl(var(--white))' }}>READY TO TEST?</h2>
          <p className="mb-6 text-lg" style={{ color: 'hsl(var(--gray))' }}>
            Create your first configuration and start testing your IAM integration.
          </p>
          <Link to="/configs" className="btn btn-gold btn-lg">
            Create Configuration
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }) {
  return (
    <div className="card-heavy p-6 bg-white">
      <div className="flex items-center space-x-3 mb-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <h3 className="font-semibold text-primary">{title}</h3>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function Step({ number, title, description }) {
  return (
    <div className="flex items-start space-x-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-black flex items-center justify-center font-semibold text-sm">
        {number}
      </div>
      <div>
        <h3 className="font-semibold mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function ProtocolCard({ title, features }) {
  return (
    <div className="card p-6" style={{ background: 'hsl(var(--charcoal))', borderColor: 'hsl(var(--charcoal))' }}>
      <h3 className="font-semibold text-lg mb-4 text-primary">{title}</h3>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center space-x-2 text-sm" style={{ color: 'hsl(var(--white))' }}>
            <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
